import { API_BASE } from "../config";
/**
 * SportsHobbiesManager.jsx
 * Manages user's sports and hobbies selections from taxonomy
 * 84 activities across 8 categories with skill level and frequency
 */

import React, { useState, useEffect } from 'react';

const SportsHobbiesManager = ({ token, fetchWithAuth }) => {
  const authFetch = fetchWithAuth || ((url, opts) => fetch(url, { ...opts, headers: { ...opts?.headers, 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } }));
  // State
  const [taxonomy, setTaxonomy] = useState([]);
  const [userSelections, setUserSelections] = useState({});
  const [expandedCategories, setExpandedCategories] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);

  // Category icons
  const categoryIcons = {
    'Outdoor': '\u{1F3D4}\uFE0F',
    'Cycling': '\u{1F6B4}',
    'Team Sports': '\u26BD',
    'Multisport': '\u{1F3CA}',
    'Winter Sports': '\u26F7\uFE0F',
    'Water Sports': '\u{1F6A3}',
    'Racket Sports': '\u{1F3BE}',
    'Combat Sports': '\u{1F94A}'
  };

  // Skill levels
  const skillLevels = [
    { value: 'beginner', label: 'Beginner' },
    { value: 'intermediate', label: 'Intermediate' },
    { value: 'advanced', label: 'Advanced' },
    { value: 'expert', label: 'Expert' },
    { value: 'professional', label: 'Professional' }
  ];

  // Frequency options
  const frequencies = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'bi_weekly', label: 'Bi-Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'seasonal', label: 'Seasonal' },
    { value: 'occasional', label: 'Occasional' }
  ];

  // Fetch taxonomy and user selections on mount
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch taxonomy
      const taxonomyRes = await authFetch(`${API_BASE}/api/profile/sports-hobbies/taxonomy`);

      if (!taxonomyRes.ok) throw new Error('Failed to fetch activities');
      const taxonomyData = await taxonomyRes.json();
      setTaxonomy(taxonomyData.categories || []);

      // Initialize all categories as collapsed
      const initialExpanded = {};
      taxonomyData.categories?.forEach(cat => {
        initialExpanded[cat.category] = false;
      });
      setExpandedCategories(initialExpanded);

      // Fetch user selections
      const selectionsRes = await authFetch(`${API_BASE}/api/profile/sports-hobbies`);

      if (!selectionsRes.ok) throw new Error('Failed to fetch selections');
      const selectionsData = await selectionsRes.json();

      // Convert to map keyed by activity_id
      const selectionsMap = {};
      selectionsData.selections?.forEach(s => {
        selectionsMap[s.activity_id] = {
          skill_level: s.skill_level || 'beginner',
          frequency: s.frequency || 'occasional',
          is_primary: s.is_primary || false,
          notes: s.notes || ''
        };
      });
      setUserSelections(selectionsMap);

    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Toggle category expansion
  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  // Expand/collapse all
  const expandAll = () => {
    const newExpanded = {};
    taxonomy.forEach(cat => {
      newExpanded[cat.category] = true;
    });
    setExpandedCategories(newExpanded);
  };

  const collapseAll = () => {
    const newExpanded = {};
    taxonomy.forEach(cat => {
      newExpanded[cat.category] = false;
    });
    setExpandedCategories(newExpanded);
  };

  // Toggle activity selection
  const toggleActivity = (activityId) => {
    setHasChanges(true);

    if (userSelections[activityId]) {
      // Remove selection
      const newSelections = { ...userSelections };
      delete newSelections[activityId];
      setUserSelections(newSelections);
      if (selectedActivity === activityId) {
        setSelectedActivity(null);
      }
    } else {
      // Add selection with defaults
      setUserSelections(prev => ({
        ...prev,
        [activityId]: {
          skill_level: 'beginner',
          frequency: 'occasional',
          is_primary: false,
          notes: ''
        }
      }));
      setSelectedActivity(activityId);
    }
  };

  // Update selection details
  const updateSelection = (activityId, field, value) => {
    setHasChanges(true);
    setUserSelections(prev => ({
      ...prev,
      [activityId]: {
        ...prev[activityId],
        [field]: value
      }
    }));
  };

  // Save changes
  const saveChanges = async () => {
    setSaving(true);
    setError(null);

    try {
      // Convert map to array format expected by API
      const selections = Object.entries(userSelections).map(([activityId, data]) => ({
        activity_id: parseInt(activityId),
        skill_level: data.skill_level,
        frequency: data.frequency,
        is_primary: data.is_primary,
        notes: data.notes
      }));

      const response = await authFetch(`${API_BASE}/api/profile/sports-hobbies`, {
        method: 'POST',
        body: JSON.stringify({ selections })
      });

      if (!response.ok) throw new Error('Failed to save selections');

      setHasChanges(false);

    } catch (err) {
      console.error('Error saving:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Get count of selected activities per category
  const getSelectionCount = (category) => {
    return category.activities.filter(a => userSelections[a.id]).length;
  };

  // Get total selected
  const getTotalSelected = () => {
    return Object.keys(userSelections).length;
  };

  // Find activity by ID across all categories
  const findActivity = (activityId) => {
    for (const cat of taxonomy) {
      const found = cat.activities.find(a => a.id === activityId);
      if (found) return { ...found, category: cat.category };
    }
    return null;
  };

  if (loading) {
    return <div className="shm-loading">Loading sports and hobbies...</div>;
  }

  const selectedActivityDetails = selectedActivity ? findActivity(selectedActivity) : null;

  return (
    <div className="shm-container">
      {/* Header */}
      <div className="shm-header">
        <div>
          <div className="shm-title">Sports & Hobbies</div>
          <div className="shm-stats">
            {getTotalSelected()} activities selected
          </div>
        </div>
        <div className="shm-headerButtons">
          <button className="shm-smallButton" onClick={expandAll}>Expand All</button>
          <button className="shm-smallButton" onClick={collapseAll}>Collapse All</button>
          <button
            className={hasChanges ? 'shm-saveButton shm-saveButton--active' : 'shm-saveButton'}
            onClick={saveChanges}
            disabled={!hasChanges || saving}
          >
            {saving ? 'Saving...' : hasChanges ? 'Save Changes' : 'Saved'}
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="shm-error">
          Error: {error}
        </div>
      )}

      {/* Categories */}
      {taxonomy.map(category => (
        <div key={category.category} className="shm-categoryCard">
          {/* Category Header */}
          <div
            className="shm-categoryHeader"
            onClick={() => toggleCategory(category.category)}
          >
            <div className="shm-categoryTitle">
              <span>{categoryIcons[category.category] || '\u{1F3C3}'}</span>
              <span>{category.category}</span>
              {getSelectionCount(category) > 0 && (
                <span className="shm-categoryCount">
                  {getSelectionCount(category)} selected
                </span>
              )}
            </div>
            <span className="shm-expandIcon">
              {expandedCategories[category.category] ? '\u25BC' : '\u25B6'}
            </span>
          </div>

          {/* Activities Grid */}
          {expandedCategories[category.category] && (
            <div className="shm-activitiesGrid">
              {category.activities.map(activity => {
                const isSelected = !!userSelections[activity.id];
                const isHighlighted = selectedActivity === activity.id;

                return (
                  <div
                    key={activity.id}
                    className={`shm-activityItem${isSelected ? ' shm-activityItem--selected' : ''}${isHighlighted ? ' shm-activityItem--highlighted' : ''}`}
                    onClick={() => toggleActivity(activity.id)}
                  >
                    <input
                      type="checkbox"
                      className="shm-checkbox"
                      checked={isSelected}
                      onChange={() => {}}
                    />
                    <span className="shm-activityName">
                      {activity.activity_name}
                    </span>
                    {activity.met_value && (
                      <span className="shm-metValue">
                        {activity.met_value} MET
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ))}

      {/* Details Panel for Selected Activity */}
      {selectedActivityDetails && userSelections[selectedActivity] && (
        <div className="shm-detailsPanel">
          <div className="shm-detailsTitle">
            <span>{categoryIcons[selectedActivityDetails.category] || '\u{1F3C3}'}</span>
            <span>{selectedActivityDetails.activity_name}</span>
            <span className="shm-detailsCategoryLabel">
              ({selectedActivityDetails.category})
            </span>
          </div>

          <div className="shm-detailsGrid">
            {/* Skill Level */}
            <div className="shm-fieldGroup">
              <label className="shm-label">Skill Level</label>
              <select
                className="shm-select"
                value={userSelections[selectedActivity].skill_level}
                onChange={(e) => updateSelection(selectedActivity, 'skill_level', e.target.value)}
              >
                {skillLevels.map(sl => (
                  <option key={sl.value} value={sl.value}>{sl.label}</option>
                ))}
              </select>
            </div>

            {/* Frequency */}
            <div className="shm-fieldGroup">
              <label className="shm-label">How Often</label>
              <select
                className="shm-select"
                value={userSelections[selectedActivity].frequency}
                onChange={(e) => updateSelection(selectedActivity, 'frequency', e.target.value)}
              >
                {frequencies.map(f => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
            </div>

            {/* Primary Activity Toggle */}
            <div className="shm-fieldGroup">
              <label className="shm-label">Primary Activity</label>
              <div
                className="shm-primaryToggle"
                onClick={() => updateSelection(selectedActivity, 'is_primary', !userSelections[selectedActivity].is_primary)}
              >
                <input
                  type="checkbox"
                  className="shm-checkbox"
                  checked={userSelections[selectedActivity].is_primary}
                  onChange={() => {}}
                />
                <span>Set as primary activity</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SportsHobbiesManager;
